import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { Email as EmailIcon, Phone as PhoneIcon } from '@mui/icons-material';
import useAnalytics from '../../hooks/useAnalytics';

interface ResourceContactFormProps {
  resourceTitle: string;
  resourceType: 'consumer' | 'professional';
  formType?: 'consultation' | 'download' | 'contact' | 'quote';
  className?: string;
}

interface FormData {
  name: string;
  email: string;
  phone: string;
  company: string;
  inquiryType: string;
  propertyAddress: string;
  message: string;
  agreeToContact: boolean;
}

const ResourceContactForm: React.FC<ResourceContactFormProps> = ({
  resourceTitle,
  resourceType,
  formType = 'contact',
  className,
}) => {
  const { trackEvent } = useAnalytics();
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    company: '',
    inquiryType: '',
    propertyAddress: '',
    message: '',
    agreeToContact: true,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null);

  const inquiryTypes = {
    consumer: [
      'Title Insurance Quote',
      'Property Closing',
      'Title Search',
      'Property History',
      'General Question',
      'Other',
    ],
    professional: [
      'Professional Services',
      'Training & Education',
      'Bulk Services',
      'Partnership Opportunity',
      'Compliance Consulting',
      'Other',
    ],
  };

  const handleInputChange = (field: keyof FormData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | any
  ) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Track the form submission
      trackEvent({
        category: 'Lead Generation',
        action: 'form_submit',
        label: `${resourceType}:${formType}:${resourceTitle}`,
      });

      // Simulate API call (replace with actual API endpoint)
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          source: 'resource_page',
          resourceTitle,
          resourceType,
          formType,
          timestamp: new Date().toISOString(),
        }),
      });

      if (response.ok) {
        setSubmitStatus('success');
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          inquiryType: '',
          propertyAddress: '',
          message: '',
          agreeToContact: true,
        });

        // Track successful conversion
        trackEvent({
          category: 'Conversion',
          action: 'form_success',
          label: `${resourceType}:${formType}:${resourceTitle}`,
        });
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
      trackEvent({
        category: 'Error',
        action: 'form_error',
        label: `${resourceType}:${formType}:${resourceTitle}`,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getFormTitle = () => {
    switch (formType) {
      case 'consultation':
        return 'Request Free Consultation';
      case 'download':
        return 'Download Resource';
      case 'quote':
        return 'Get a Quote';
      default:
        return 'Contact Us';
    }
  };

  const getFormDescription = () => {
    if (resourceType === 'professional') {
      return 'Connect with our professional services team for expert title solutions.';
    }
    return 'Get personalized assistance with your real estate transaction.';
  };

  return (
    <Card className={className} sx={{ mt: 4, border: '2px solid #e2e8f0' }}>
      <CardContent sx={{ p: 4 }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, color: '#1e3a8a', mb: 1 }}>
            {getFormTitle()}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {getFormDescription()}
          </Typography>
        </Box>

        {submitStatus === 'success' && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Thank you! We'll contact you within 24 hours.
          </Alert>
        )}

        {submitStatus === 'error' && (
          <Alert severity="error" sx={{ mb: 3 }}>
            There was an error submitting your request. Please try again or call us directly.
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <Box sx={{ display: 'grid', gap: 3 }}>
            {/* Name and Email Row */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <TextField
                fullWidth
                label="Full Name"
                required
                value={formData.name}
                onChange={handleInputChange('name')}
                disabled={isSubmitting}
              />
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                required
                value={formData.email}
                onChange={handleInputChange('email')}
                disabled={isSubmitting}
              />
            </Box>

            {/* Phone and Company Row */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <TextField
                fullWidth
                label="Phone Number"
                value={formData.phone}
                onChange={handleInputChange('phone')}
                disabled={isSubmitting}
              />
              {resourceType === 'professional' && (
                <TextField
                  fullWidth
                  label="Company/Organization"
                  value={formData.company}
                  onChange={handleInputChange('company')}
                  disabled={isSubmitting}
                />
              )}
            </Box>

            {/* Inquiry Type */}
            <FormControl fullWidth>
              <InputLabel>Type of Inquiry</InputLabel>
              <Select
                value={formData.inquiryType}
                label="Type of Inquiry"
                onChange={handleInputChange('inquiryType')}
                disabled={isSubmitting}
              >
                {inquiryTypes[resourceType].map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Property Address (for consumer forms) */}
            {resourceType === 'consumer' && (
              <TextField
                fullWidth
                label="Property Address (Optional)"
                value={formData.propertyAddress}
                onChange={handleInputChange('propertyAddress')}
                disabled={isSubmitting}
                helperText="Include the property address if your inquiry relates to a specific property"
              />
            )}

            {/* Message */}
            <TextField
              fullWidth
              label="Message"
              multiline
              rows={4}
              value={formData.message}
              onChange={handleInputChange('message')}
              disabled={isSubmitting}
              placeholder={`Tell us more about your ${resourceType === 'professional' ? 'business needs' : 'real estate transaction'}...`}
            />

            {/* Consent Checkbox */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.agreeToContact}
                  onChange={handleInputChange('agreeToContact')}
                  disabled={isSubmitting}
                />
              }
              label="I agree to be contacted by MAD Title Agency regarding my inquiry"
            />

            {/* Submit Button */}
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={isSubmitting || !formData.agreeToContact}
              sx={{
                mt: 2,
                py: 1.5,
                background: resourceType === 'professional'
                  ? 'linear-gradient(135deg, #059669 0%, #10b981 100%)'
                  : '#1e3a8a',
                '&:hover': {
                  background: resourceType === 'professional'
                    ? 'linear-gradient(135deg, #047857 0%, #059669 100%)'
                    : '#1e40af',
                },
              }}
              startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <EmailIcon />}
            >
              {isSubmitting ? 'Submitting...' : 'Send Message'}
            </Button>
          </Box>
        </Box>

        {/* Contact Information */}
        <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid #e2e8f0', textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Prefer to call? We're here to help:
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
            <PhoneIcon sx={{ color: '#6b7280', fontSize: '1.2rem' }} />
            <Typography variant="h6" sx={{ color: '#1e3a8a', fontWeight: 600 }}>
              (732) 874-5425
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ResourceContactForm; 